import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import HomeService from "../Services/HomeService";
import { useNavigate } from "react-router-dom";

const headLineStyle = {
  textAlign: "center",
  fontSize: "30px",
  color: "rgb(131, 144, 155)",
  margin: "0px 0px 10px 0px",
  fontWeight: "bold",
};

function AddAdvertisementShowingOrder() {
  const [sequenceNumber, setSequenceNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasExistingOrder, setHasExistingOrder] = useState(false);
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();

  // Check if advertisement showing order already exists
  useEffect(() => {
    checkExistingOrder();
  }, []);

  const checkExistingOrder = async () => {
    setChecking(true);
    try {
      const res = await HomeService.getAdvertisementShowingOrder();
      if (res?.data && res.data.length > 0) {
        setHasExistingOrder(true);
        toast.error(
          "An advertisement showing order already exists. Please edit or delete the existing one."
        );
        setTimeout(() => {
          navigate("/manage-advertisement-showing-order");
        }, 2000);
      }
    } catch (error) {
      console.error("Error checking existing order:", error);
    } finally {
      setChecking(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check again if order exists
    if (hasExistingOrder) {
      toast.error("An order already exists. Redirecting to manage page...");
      setTimeout(() => {
        navigate("/manage-advertisement-showing-order");
      }, 1500);
      return;
    }

    // Validation
    if (!sequenceNumber) {
      toast.error("Please enter a sequence number");
      return;
    }

    const seqNum = parseInt(sequenceNumber);
    if (isNaN(seqNum) || seqNum < 1) {
      toast.error("Please enter a valid positive number");
      return;
    }

    setLoading(true);

    // Prepare data according to Postman body structure
    const orderData = {
      sequenceNumber: seqNum,
    };

    console.log("Advertisement Showing Order Data:", orderData);

    try {
      const res = await HomeService.createAdvertisementShowingOrder(orderData);
      if (res?.status) {
        toast.success("Advertisement showing order added successfully!");
        clearForm();
        // Redirect to manage page after successful creation
        setTimeout(() => {
          navigate("/manage-advertisement-showing-order");
        }, 1500);
      } else {
        toast.error(
          res?.message || "Failed to add advertisement showing order."
        );
      }
    } catch (error) {
      toast.error("Failed to add advertisement showing order.");
      console.error("Error adding advertisement showing order:", error);
    } finally {
      setLoading(false);
    }
  };

  const clearForm = () => {
    setSequenceNumber("");
  };

  const handleGoToManage = () => {
    navigate("/manage-advertisement-showing-order");
  };

  if (checking) {
    return (
      <div className="main_wrap" style={{ marginTop: "6rem" }}>
        <div className="container-fluid">
          <div className="mt-5">
            <div style={headLineStyle} className="page-headline">
              Add Advertisement Showing Order
            </div>
            <hr />
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Checking for existing order...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (hasExistingOrder) {
    return (
      <div className="main_wrap" style={{ marginTop: "6rem" }}>
        <div className="container-fluid">
          <div className="mt-5">
            <div style={headLineStyle} className="page-headline">
              Add Advertisement Showing Order
            </div>
            <hr />
            <div className="card">
              <div className="card-body text-center py-5">
                <div className="alert alert-warning mb-4">
                  <i className="fa-solid fa-exclamation-triangle fa-2x mb-3"></i>
                  <h4>Advertisement Showing Order Already Exists!</h4>
                  <p className="mb-0">
                    An advertisement showing order already exists in the system.
                    You cannot create multiple orders. Please edit or delete the
                    existing one.
                  </p>
                </div>
                <button
                  className="btn btn-primary"
                  onClick={handleGoToManage}
                  style={{
                    background: "rgb(133, 218, 79)",
                    border: "none",
                    padding: "10px 30px",
                    fontSize: "16px",
                    fontWeight: "500",
                    borderRadius: "13px",
                  }}
                >
                  <i className="fa-solid fa-cog me-2"></i>
                  Go to Manage Advertisement Order
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main_wrap" style={{ marginTop: "6rem" }}>
      <div className="container-fluid">
        <div className="mt-5">
          <div style={headLineStyle} className="page-headline">
            Add Advertisement Showing Order
          </div>
          <hr />

          <section className="chartBox_area">
            <form onSubmit={handleSubmit}>
              <div className="row">
                {/* Sequence Number Input */}
                <div className="col-md-12 mt-3">
                  <label htmlFor="sequenceNumber">
                    Sequence Number
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    id="sequenceNumber"
                    className="form-control"
                    value={sequenceNumber}
                    onChange={(e) => setSequenceNumber(e.target.value)}
                    min="1"
                    placeholder="Enter sequence number (e.g., 5)"
                    required
                  />
                  <div className="form-text">
                    Enter the sequence number for advertisement display order
                    (positive integer)
                  </div>
                </div>

                {/* Information Note */}
                <div className="col-md-12 mt-4">
                  <div className="alert alert-info">
                    <h6 className="alert-heading">
                      <i className="fa-solid fa-info-circle me-2"></i>
                      Important Note
                    </h6>
                    <p className="mb-0">
                      Only one advertisement showing order can be created. Once
                      created, you can edit or delete it from the manage page.
                      After creating the order, you will be redirected to the
                      manage page. The sequence number determines the order in
                      which advertisements are displayed.
                    </p>
                  </div>
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="row mt-4">
                <div className="col-12">
                  <button
                    type="submit"
                    className="btn mt-3"
                    style={{
                      background: "rgb(133, 218, 79)",
                      borderRadius: "13px",
                      padding: "10px 30px",
                      fontSize: "16px",
                      fontWeight: "500",
                    }}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin me-2"></i>
                        Adding Order...
                      </>
                    ) : (
                      "Add Advertisement Showing Order"
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary mt-3 ml-2"
                    style={{
                      borderRadius: "13px",
                      padding: "10px 30px",
                      fontSize: "16px",
                      fontWeight: "500",
                    }}
                    onClick={clearForm}
                    disabled={!sequenceNumber}
                  >
                    Clear
                  </button>
                </div>
              </div>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
}

export default AddAdvertisementShowingOrder;
